import React from 'react'
import Plot from 'react-plotly.js'
import { Card, CardContent, Typography } from '@mui/material'
import './VKGLClassificationChart.scss'

export default function VKGLClassificationChart({ data }) {
    if (!data || !data.multiLab || !data.singleLab) {
        return (
            <Card className="vkgl-chart-card">
                <CardContent>
                    <Typography variant="h6">HGQN Variantenklassifikation</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Keine Daten verfügbar
                    </Typography>
                </CardContent>
            </Card>
        )
    }

    const { multiLab, singleLab } = data

    // Calculate totals for display
    const multiLabTotal = multiLab.benign + multiLab.pathogenic + multiLab.vus + multiLab.noConsensusConflicting
    const singleLabTotal = singleLab.benign + singleLab.pathogenic + singleLab.vus

    // Color scheme matching the pie chart
    const colors = {
        benign: '#388e3c',           // Dark green - (Likely) Benign (matches pie chart benign)
        pathogenic: '#d32f2f',       // Dark red - (Likely) Pathogenic (matches pie chart pathogenic)
        vus: '#fbc02d',              // Yellow - VUS (matches pie chart uncertain)
        noConsensus: '#000000'       // Black - No consensus/conflicting
    }

    const config = {
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
        modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d']
    }

    // Create data for separate grouped charts
    const multiLabGroupedData = [
        {
            name: '(Wahrsch.) Benign',
            x: ['(Wahrsch.) Benign'],
            y: [multiLab.benign],
            type: 'bar',
            marker: { color: colors.benign },
            hovertemplate: '%{y} Varianten<br>(Wahrsch.) Benign<extra></extra>'
        },
        {
            name: '(Wahrsch.) Pathogen',
            x: ['(Wahrsch.) Pathogen'],
            y: [multiLab.pathogenic],
            type: 'bar',
            marker: { color: colors.pathogenic },
            hovertemplate: '%{y} Varianten<br>(Wahrsch.) Pathogen<extra></extra>'
        },
        {
            name: 'VUS',
            x: ['VUS'],
            y: [multiLab.vus],
            type: 'bar',
            marker: { color: colors.vus },
            hovertemplate: '%{y} Varianten<br>VUS<extra></extra>'
        },
        {
            name: 'Kein Konsensus / Konflikt',
            x: ['Kein Konsensus / Konflikt'],
            y: [multiLab.noConsensusConflicting],
            type: 'bar',
            marker: { color: colors.noConsensus },
            hovertemplate: '%{y} Varianten<br>Kein Konsensus / Konflikt<extra></extra>'
        }
    ]

    const singleLabGroupedData = [
        {
            name: '(Wahrsch.) Benign',
            x: ['(Wahrsch.) Benign'],
            y: [singleLab.benign],
            type: 'bar',
            marker: { color: colors.benign },
            hovertemplate: '%{y} Varianten<br>(Wahrsch.) Benign<extra></extra>',
            showlegend: false
        },
        {
            name: '(Wahrsch.) Pathogen',
            x: ['(Wahrsch.) Pathogen'],
            y: [singleLab.pathogenic],
            type: 'bar',
            marker: { color: colors.pathogenic },
            hovertemplate: '%{y} Varianten<br>(Wahrsch.) Pathogen<extra></extra>',
            showlegend: false
        },
        {
            name: 'VUS',
            x: ['VUS'],
            y: [singleLab.vus],
            type: 'bar',
            marker: { color: colors.vus },
            hovertemplate: '%{y} Varianten<br>VUS<extra></extra>',
            showlegend: false
        }
    ]

    const multiLabGroupedLayout = {
        title: {
            text: 'Varianten von 2+ Laboren',
            font: { size: 16 }
        },
        barmode: 'group',
        height: 450,
        xaxis: {
            tickfont: { size: 11 }
        },
        yaxis: {
            title: {
                text: 'Anzahl Varianten (log)',
                font: { size: 13 }
            },
            type: 'log',
            gridcolor: '#e0e0e0'
        },
        legend: {
            orientation: 'h',
            y: -0.25,
            x: 0.5,
            xanchor: 'center',
            font: { size: 11 }
        },
        margin: {
            l: 80,
            r: 50,
            t: 60,
            b: 110
        },
        paper_bgcolor: 'white',
        plot_bgcolor: 'white',
        hovermode: 'closest'
    }

    const singleLabGroupedLayout = {
        title: {
            text: 'Varianten von 1 Labor',
            font: { size: 16 }
        },
        barmode: 'group',
        height: 450,
        xaxis: {
            tickfont: { size: 11 }
        },
        yaxis: {
            title: {
                text: 'Anzahl Varianten (log)',
                font: { size: 13 }
            },
            type: 'log',
            gridcolor: '#e0e0e0'
        },
        showlegend: false,
        margin: {
            l: 80,
            r: 50,
            t: 60,
            b: 110
        },
        paper_bgcolor: 'white',
        plot_bgcolor: 'white',
        hovermode: 'closest'
    }

    // Create stacked versions with separate charts for optimal ordering
    const categories = [
        {
            name: '(Wahrsch.) Benign',
            multiLabValue: multiLab.benign,
            singleLabValue: singleLab.benign,
            color: colors.benign
        },
        {
            name: '(Wahrsch.) Pathogen',
            multiLabValue: multiLab.pathogenic,
            singleLabValue: singleLab.pathogenic,
            color: colors.pathogenic
        },
        {
            name: 'VUS',
            multiLabValue: multiLab.vus,
            singleLabValue: singleLab.vus,
            color: colors.vus
        },
        {
            name: 'Kein Konsensus / Konflikt',
            multiLabValue: multiLab.noConsensusConflicting,
            singleLabValue: 0,
            color: colors.noConsensus
        }
    ]

    // Sort for multi-lab (left bar) - smallest at bottom for log scale
    const multiLabSorted = [...categories].sort((a, b) => a.multiLabValue - b.multiLabValue)
    const multiLabPlotData = multiLabSorted.map(cat => ({
        name: cat.name,
        x: ['Varianten von 2+ Laboren'],
        y: [cat.multiLabValue],
        type: 'bar',
        marker: {
            color: cat.color
        },
        hovertemplate: '%{y} Varianten<br>' + cat.name + '<extra></extra>'
    }))

    // Sort for single-lab (right bar) - smallest at bottom for log scale
    const singleLabSorted = [...categories].sort((a, b) => a.singleLabValue - b.singleLabValue)
    const singleLabPlotData = singleLabSorted.map(cat => ({
        name: cat.name,
        x: ['Varianten von 1 Labor'],
        y: [cat.singleLabValue],
        type: 'bar',
        marker: {
            color: cat.color
        },
        hovertemplate: '%{y} Varianten<br>' + cat.name + '<extra></extra>',
        showlegend: false  // Hide legend for second chart to avoid duplication
    }))

    const stackedLayoutMultiLab = {
        title: {
            text: 'Varianten von 2+ Laboren (Gestapelt)',
            font: {
                size: 16
            }
        },
        barmode: 'stack',
        height: 450,
        xaxis: {
            tickfont: {
                size: 12
            }
        },
        yaxis: {
            title: {
                text: 'Anzahl Varianten (log)',
                font: {
                    size: 13
                }
            },
            type: 'log',
            gridcolor: '#e0e0e0'
        },
        legend: {
            orientation: 'h',
            y: -0.2,
            x: 0.5,
            xanchor: 'center',
            font: {
                size: 11
            }
        },
        margin: {
            l: 80,
            r: 50,
            t: 60,
            b: 100
        },
        paper_bgcolor: 'white',
        plot_bgcolor: 'white',
        hovermode: 'closest'
    }

    const stackedLayoutSingleLab = {
        title: {
            text: 'Varianten von 1 Labor (Gestapelt)',
            font: {
                size: 16
            }
        },
        barmode: 'stack',
        height: 450,
        xaxis: {
            tickfont: {
                size: 12
            }
        },
        yaxis: {
            title: {
                text: 'Anzahl Varianten (log)',
                font: {
                    size: 13
                }
            },
            type: 'log',
            gridcolor: '#e0e0e0'
        },
        showlegend: false,  // Legend shown in first chart only
        margin: {
            l: 80,
            r: 50,
            t: 60,
            b: 100
        },
        paper_bgcolor: 'white',
        plot_bgcolor: 'white',
        hovermode: 'closest'
    }

    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <Card className="vkgl-chart-card">
                    <CardContent>
                        <Plot
                            data={multiLabGroupedData}
                            layout={multiLabGroupedLayout}
                            config={config}
                            className="vkgl-plot"
                            style={{ width: '100%' }}
                        />
                        <div className="chart-summary">
                            <Typography variant="caption" color="textSecondary">
                                <strong>Gruppiert:</strong> Jede Kategorie separat mit eigener logarithmischer Skala | {' '}
                                {multiLabTotal.toLocaleString('de-DE')} Varianten gesamt
                            </Typography>
                        </div>
                    </CardContent>
                </Card>

                <Card className="vkgl-chart-card">
                    <CardContent>
                        <Plot
                            data={singleLabGroupedData}
                            layout={singleLabGroupedLayout}
                            config={config}
                            className="vkgl-plot"
                            style={{ width: '100%' }}
                        />
                        <div className="chart-summary">
                            <Typography variant="caption" color="textSecondary">
                                <strong>Gruppiert:</strong> Jede Kategorie separat mit eigener logarithmischer Skala | {' '}
                                {singleLabTotal.toLocaleString('de-DE')} Varianten gesamt
                            </Typography>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/*<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>*/}
            {/*    <Card className="vkgl-chart-card">*/}
            {/*        <CardContent>*/}
            {/*            <Plot*/}
            {/*                data={multiLabPlotData}*/}
            {/*                layout={stackedLayoutMultiLab}*/}
            {/*                config={config}*/}
            {/*                className="vkgl-plot"*/}
            {/*                style={{ width: '100%' }}*/}
            {/*            />*/}
            {/*            <div className="chart-summary">*/}
            {/*                <Typography variant="caption" color="textSecondary">*/}
            {/*                    <strong>Gestapelt:</strong> Kleinste Werte unten für optimale Sichtbarkeit | {' '}*/}
            {/*                    {multiLabTotal.toLocaleString('de-DE')} Varianten gesamt*/}
            {/*                </Typography>*/}
            {/*            </div>*/}
            {/*        </CardContent>*/}
            {/*    </Card>*/}

            {/*    <Card className="vkgl-chart-card">*/}
            {/*        <CardContent>*/}
            {/*            <Plot*/}
            {/*                data={singleLabPlotData}*/}
            {/*                layout={stackedLayoutSingleLab}*/}
            {/*                config={config}*/}
            {/*                className="vkgl-plot"*/}
            {/*                style={{ width: '100%' }}*/}
            {/*            />*/}
            {/*            <div className="chart-summary">*/}
            {/*                <Typography variant="caption" color="textSecondary">*/}
            {/*                    <strong>Gestapelt:</strong> Kleinste Werte unten für optimale Sichtbarkeit | {' '}*/}
            {/*                    {singleLabTotal.toLocaleString('de-DE')} Varianten gesamt*/}
            {/*                </Typography>*/}
            {/*            </div>*/}
            {/*        </CardContent>*/}
            {/*    </Card>*/}
            {/*</div>*/}
        </>
    )
}
